import { UUID } from "io-ts-types";
import { NextPageContext } from "next";

import { SPECIAL_FUNDRAISER_NONPROFIT_SLUG } from "@every.org/common/src/entity/constants";
import {
  getRoutePath,
  ClientRouteName,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { StaticApiRouteSpec } from "@every.org/common/src/routes";
import { getNonprofitRouteSpec } from "@every.org/common/src/routes/nonprofit";

import CreateFundraiserPage from "src/pages/Fundraiser/CreatePage";
import { createGetServerSideProps } from "src/utilities/ssr/createGetServerSideProps";
import { validateAndDecodeParam } from "src/utilities/ssr/validateAndDecodeParam";
import { withPropsDeserialization } from "src/utilities/ssr/withPropsDeserialization";
import { serversideStaticQueryApi } from "src/utility/apiClient";

const fetchNonprofit = async (identifier: string, ctx: NextPageContext) => {
  const { nonprofit } = await serversideStaticQueryApi(
    getNonprofitRouteSpec as StaticApiRouteSpec,
    {
      routeTokens: { identifier },
      queryParams: {},
    },
    ctx
  );
  return nonprofit;
};

export const getServerSideProps = createGetServerSideProps(
  async (ctx: NextPageContext) => {
    const slug = validateAndDecodeParam(ctx, "nonprofitSlugOrUsername").trim();

    const forNonprofitsString = ctx.query.forNonprofits;

    const forNonprofits: UUID[] =
      forNonprofitsString && typeof forNonprofitsString === "string"
        ? (forNonprofitsString.split(",") as UUID[])
        : [];

    try {
      // If just one nonprofit is passed in the query, we can redirect to the create fundraiser page for that nonprofit
      if (forNonprofits.length === 1) {
        const nonprofit = await fetchNonprofit(forNonprofits[0], ctx);

        return {
          redirect: {
            destination: getRoutePath({
              name: ClientRouteName.FUNDRAISER_CREATE,
              tokens: {
                nonprofitSlug: nonprofit.primarySlug,
              },
              format: URLFormat.RELATIVE,
            }),
            permanent: true,
          },
        };
      }

      const nonprofits = [];
      const nonprofit = await fetchNonprofit(slug, ctx);

      nonprofits.push(nonprofit);

      // Only pass ids that resolved, so unknown ids aren't posted
      let resolvedForNonprofits = forNonprofits;
      if (slug === SPECIAL_FUNDRAISER_NONPROFIT_SLUG) {
        if (forNonprofits) {
          const fetchedNonprofits = await Promise.all(
            forNonprofits.map(async (id) => {
              try {
                return await fetchNonprofit(id, ctx);
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Failed to fetch nonprofit with ID: ${id}`, e);
                return null;
              }
            })
          );
          resolvedForNonprofits = fetchedNonprofits.flatMap((n) =>
            n ? [n.id] : []
          );

          nonprofits.push(
            ...fetchedNonprofits.filter((n) => n?.donationsEnabled)
          );
        }
      }

      return {
        props: {
          slug,
          nonprofit,
          forNonprofits: resolvedForNonprofits,
          initialData: {
            nonprofits,
          },
        },
      };
    } catch (error) {
      return { props: {} };
    }
  }
);

export default withPropsDeserialization(CreateFundraiserPage);
